import { useEffect, useRef, useState } from 'react';
import { useErrorNotice } from './useErrorNotice';
import { TerraDrawManager } from '../common/TerraDrawManager';
import { readFileAndGetCoordinates } from '../app/components/visualization/shoreR/utils';
import { BASELINE_VERTEX_EDITING_ENABLED, useShapeVertexEditing } from './useShapeVertexEditing';

export const useBaselineDrawing = ({ mapRef, roiCoords, drawingManager, t, onConfirmed, initialBaseLineCoords }) => {
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();
  const [baseLine, setBaseLine] = useState(null);
  const [baseLineCoords, setBaseLineCoords] = useState([]);
  const [confirmDrawing, setConfirmDrawing] = useState(false);
  const restored = useRef(false);

  useEffect(() => {
    if (restored.current || !mapRef.current || !initialBaseLineCoords || initialBaseLineCoords.length < 2) return;
    restored.current = true;
    const path = initialBaseLineCoords.map(([lng, lat]) => ({ lat, lng }));
    const polyline = new window.google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: '#03d4f0ff',
      strokeOpacity: 1,
      strokeWeight: 3,
      map: mapRef.current,
    });
    setBaseLine({ overlay: polyline });
    setBaseLineCoords(initialBaseLineCoords);
    setConfirmDrawing(true);
    onConfirmed?.(initialBaseLineCoords);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBaseLineCoords]);

  /**
   * @description Lets the user drag the drawn baseline's existing points, re-checking after each
   * adjustment that it still lies inside the AOI — the same rule `handleDrawing` enforces when the
   * baseline is first drawn.
   * @see {@link useShapeVertexEditing}
   * @see {@link areCoordinatesInsideAOI}
   * @memberof module:Shoreline
   */
  const editing = useShapeVertexEditing({
    onChange: (coords) => {
      setBaseLineCoords(coords);
      onConfirmed?.(coords);
    },
    validate: (coords) => (areCoordinatesInsideAOI(coords) ? '' : t('forms.map.editOutsideAOI')),
    enabled: BASELINE_VERTEX_EDITING_ENABLED,
  });

  /**
   * @function areCoordinatesInsideAOI
   * @description Checks whether every `[lng, lat]` pair falls inside the region of interest. The
   * coordinate-based counterpart of `isPolygonInsideAOI`, used while editing, when the new vertices
   * arrive as plain coordinates rather than as an overlay.
   * @param {Array<Array<Number>>} coords - Baseline coordinates as [lng, lat] pairs.
   * @see {@link isPolygonInsideAOI}
   * @memberof module:Shoreline
   * @returns {Boolean} True when the whole baseline is inside the AOI.
   */
  function areCoordinatesInsideAOI(coords) {
    if (!roiCoords.current) return true;

    return coords.every(([lng, lat]) =>
      window.google.maps.geometry?.poly.containsLocation(new window.google.maps.LatLng(lat, lng), roiCoords.current),
    );
  }

  /**
   * @function isPolygonInsideAOI
   * @description Validates if one polygon is fully contained within another polygon.
   * 1. Iterates over all vertices of the inner polygon
   * 2. Checks if each vertex is contained within the outer polygon
   * 3. Returns `true` if fully contained, otherwise `false`
   * @param {Object} polygonInner = Polygon that must be inside, ex : the baseline.
   * @param {Object} polygonOuter = Polygon that must contain the other one, ex: the area of interest (AOI)
   * **Called by:
   * @see {handleDrawing} - This function calls `isPolygonInsideAOI` to validate the baseline geometry.
   * @memberof module:Shoreline
   * @returns {Boolean} True if the inner polygon is fully inside the outer one, else False
   */
  function isPolygonInsideAOI(polygonInner, polygonOuter) {
    for (let i = 0; i < polygonInner.getPath().getLength(); i++) {
      if (!window.google.maps.geometry?.poly.containsLocation(polygonInner.getPath().getAt(i), polygonOuter)) {
        return false;
      }
    }
    return true;
  }

  function handleDrawing(polyg) {
    if (isPolygonInsideAOI(polyg.overlay, roiCoords.current)) {
      setConfirmDrawing(true);
      setBaseLine(polyg);
      drawingManager.current?.setDrawingMode(null);
      const coords = polyg.overlay
        .getPath()
        .getArray()
        .map((c) => [c.lng(), c.lat()]);
      setBaseLineCoords(coords);
      editing.setShape(polyg.overlay, 'polyline');
      onConfirmed?.(coords);
      return;
    }
    notifyError(t('forms.shorelineAnalysis.baselineAOIDrawAlert'));
    polyg.overlay.setMap(null);
  }

  /**
   * @function handleDrawBaseline
   * @description
   * Prepares and enables baseline drawing on the map using the Google Maps DrawingManager.
   * 1. Creates a DrawingManager instance if it does not already exist, configured for polyline drawing
   * 2. Attaches the DrawingManager to the current map reference (`mapRef.current`)
   * 3. Registers an "overlaycomplete" listener that triggers `handleDrawing` when the user finishes drawing.
   * 4. Clears any previously drawn baseline from the map and resets its state.
   *
   * @see {@link handleDrawing} Called when "overlaycomplete" to validate and process the drawn baseline.
   * @requires GoogleMaps JavaScript API with the Drawing library loaded.
   * **Called by:
   * @see {handleRedo}
   * @see {CoastlineAnalysisSteps} - via the "draw baseline" button's onClick.
   * @memberof module:Shoreline
   * @returns {void}
   */
  const handleDrawBaseline = () => {
    if (!drawingManager.current) {
      drawingManager.current = new TerraDrawManager({
        drawingMode: 'polyline',
        drawingControl: false,
        polylineOptions: {
          strokeColor: '#03d4f0ff',
          strokeOpacity: 1,
          strokeWeight: 3,
        },
      });
      drawingManager.current.setMap(mapRef.current);
      drawingManager.current.addListener('overlaycomplete', handleDrawing);
    } else {
      drawingManager.current.setDrawingMode('polyline');
    }
    if (baseLine) baseLine.overlay.setMap(null);
    editing.setShape(null);
    setBaseLine(null);
  };

  /**
   * @function handleRedo
   * @description
   * Clear current baseline to draw/create it again.
   * 1. Removes all event listeners from the current drawing manager
   * 2. Clears the drawing manager from the map
   * 3. Removes the existing baseline overlay from the map
   * 4. Resets state variables to allow new baseline creation
   * 5. Reinitializes the drawing manager so the user can draw the baseline again
   *
   * @see {@link handleDrawBaseline} - Called to reinitialize the drawing manager
   * **Called by:
   * @see {CoastlineAnalysisSteps} - via the "Redo" button's onClick.
   * @memberof module:Shoreline
   * @returns {void}
   */
  const handleRedo = () => {
    if (drawingManager.current) {
      drawingManager.current.cleanup();
      drawingManager.current.setMap(null);
      drawingManager.current = null;
    }
    baseLine && baseLine.overlay.setMap(null);
    editing.setShape(null);
    setBaseLine(null);
    setConfirmDrawing(false);
    handleDrawBaseline();
  };

  /**
   * @function readBaselineFileData
   * @description
   * Reads a KML file containing baseline coordinates, parses the coordinates,
   * and draws the baseline on the map.
   * @param {Object} e - The file input change event containing the selected file.
   * @see {@link readFileAndGetCoordinates} - Called to parse KML file content and extract coordinates.
   * @see {@link handleDrawing} - Called to process and display the drawn baseline on the map.
   * **Called by:
   * @see {CoastlineAnalysisSteps} - via the baseline KML file input's onChange.
   * @memberof module:Shoreline
   * @returns {void}
   */
  const readBaselineFileData = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    try {
      reader.onload = (event) => {
        const baselineFile = readFileAndGetCoordinates(reader.result, t, notifyError);
        if (baselineFile.length === 0) {
          notifyError(t('forms.csqueeze.2.errorMessageNoValidCoord'));
          return;
        }
        const formattedCoordinates = baselineFile[0].map((coord) => ({
          lat: coord[1],
          lng: coord[0],
        }));
        const polyline = new window.google.maps.Polyline({
          path: formattedCoordinates,
          strokeColor: '#03d4f0ff',
          strokeOpacity: 1,
          strokeWeight: 3,
          map: mapRef.current,
        });

        handleDrawing({ overlay: polyline });
      };
      if (file) reader.readAsText(file);
    } catch (error) {
      console.error('Error reading baseline file:', error);
      notifyError(t('forms.csqueeze.2.errorMessageUnableInterpretKml'));
    }
  };

  return {
    baseLine,
    baseLineCoords,
    confirmDrawing,
    editing,
    setBaseLineCoords,
    setConfirmDrawing,
    setBaseLine,
    handleDrawBaseline,
    handleRedo,
    readBaselineFileData,
  };
};
